import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  BoxProps,
  Divider,
  IconProps,
  Typography,
} from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { Tooltip } from '../../data-display/tooltip';

export type CollapsibleCardProps = {
  title: string;
  infoText?: string;
  description?: string;
  children: React.ReactNode;
  Icon: React.ComponentType<IconProps | SvgIconProps>;
  iconColor: string;
  iconBackgroundColor: BoxProps['bgcolor'];
  rightSlot: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
};

export const CollapsibleCard = React.forwardRef<HTMLDivElement, CollapsibleCardProps>(
  (
    {
      title,
      description,
      children,
      Icon,
      iconColor,
      iconBackgroundColor,
      rightSlot,
      expanded,
      onChange,
      infoText,
    },
    ref,
  ) => {
    return (
      <Accordion
        sx={{
          '&.MuiAccordion-root.Mui-expanded + .MuiAccordion-root:before': { display: 'none' },
          '&.MuiAccordion-root:first-of-type': (theme) => ({
            borderTopLeftRadius: theme.shape.borderRadius * 2,
            borderTopRightRadius: theme.shape.borderRadius * 2,
          }),
          '&.MuiAccordion-root:last-of-type': (theme) => ({
            borderBottomLeftRadius: theme.shape.borderRadius * 2,
            borderBottomRightRadius: theme.shape.borderRadius * 2,
          }),
          '&.MuiAccordion-root.Mui-expanded': (theme) => ({
            marginBottom: theme.spacing(1),
          }),
          '&.MuiAccordion-root.Mui-expanded:not(:first-of-type)': (theme) => ({
            marginTop: theme.spacing(1),
          }),
        }}
        elevation={1}
        ref={ref}
        expanded={expanded}
        onChange={onChange}
      >
        <AccordionSummary
          sx={{
            '& .MuiAccordionSummary-content': { alignItems: 'center', marginY: 2 },
            '&.MuiAccordionSummary-root': { px: 3 },
          }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Box
            p={1}
            bgcolor={iconBackgroundColor}
            borderRadius='50%'
            display='flex'
            alignItems='center'
          >
            <Icon sx={{ color: iconColor }} />
          </Box>
          <Box ml={1} display='flex' flexDirection='column' justifyContent='center'>
            <Box display='flex'>
              <Typography variant='subtitle1'>{title}</Typography>
              {infoText && (
                <Tooltip title={infoText} placement='top'>
                  <InfoOutlinedIcon fontSize='small' color='action' sx={{ ml: 0.5 }} />
                </Tooltip>
              )}
            </Box>

            <Typography variant='body2' color='text.secondary'>
              {description}
            </Typography>
          </Box>
          {rightSlot && <Box ml='auto'>{rightSlot}</Box>}
        </AccordionSummary>
        <AccordionDetails sx={{ '&.MuiAccordionDetails-root': { px: 3, pt: 0, pb: 2 } }}>
          <Divider sx={{ mb: 3 }} />
          {children}
        </AccordionDetails>
      </Accordion>
    );
  },
);
